/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'

import {
  WorkflowRunSteps,
  ActionsSummaryBar,
  ButtonRegeneratePayrollCycle,
  ButtonResendAmountApprovalEmail,
  ButtonResendPaymentApprovalEmail,
  ButtonUploadDeductionSpreadsheet,
  ButtonDownloadDeductionSpreadsheet,
  DeductionUploadConfirm,
  Modal,
  Warning,
  Button,
  Heading,
  Spinner,
  SvgIcon
} from '../'


export default class PayrollRunSummaryView extends React.PureComponent {
  static propTypes = {
    payrollRunId: PropTypes.string.isRequired,
    workflowRunDeducting: PropTypes.object,
    workflowRunReceipting: PropTypes.object,
    paymentsSummary: PropTypes.object.isRequired,
    paymentsTotal: PropTypes.string,
    uploadDisabled: PropTypes.bool.isRequired,
    refetchData: PropTypes.func.isRequired
  }

  state = {
    showDownloadSuccessModal: false,
    showProcessingUpload: false,
    uploadError: false,
    uploadConfirm: false
  }

  showDownloadSuccessModal = () => (
    <Modal
      body={
        <div className='slds-align_absolute-center slds-size_2-of-3' style={{ display: 'block' }}>
          <div className='slds-m-bottom_large slds-align_absolute-center'>
            <SvgIcon
              variant='utility'
              symbol='check'
              size='large'
              colour='success'
            />
          </div>
          <div>
            <Heading className='slds-text-align_center'>
              {`The spreadsheet has been emailed to ${localStorage.getItem('userEmailAddress')}.  Should you not receive it within 10 minutes, please contact us`}
            </Heading>
          </div>
        </div>
      }
      footer={
        <Button
          type='brand'
          onClick={() => this.setState({ showDownloadSuccessModal: false })}
          className='slds-align_absolute-center'>
          OK
        </Button>
      }
    />
  )

  showUploadConfirmModal = () => (
    <DeductionUploadConfirm
      file={this.state.fileUploaded}
      changes={this.state.uploadChanges}
      prevented={this.state.uploadPrevented}
      errors={this.state.uploadErrors}
      ambiguous={this.state.uploadAmbiguous}
      onCancel={() => this.setState({
        uploadConfirm: false,
        fileUploaded: null
      })}
      onSaved={() => this.setState(
        {
          uploadConfirm: false,
          fileUploaded: null
        }, this.props.refetchData
      )}
    />
  )

  showUploadError = () => (
    <Modal
      body={
        <div>
          <Warning className='slds-align_absolute-center slds-m-bottom_medium' iconSize='medium'>
            <Heading className='slds-m-top_medium'>
              File upload failed.
            </Heading>
          </Warning>
        </div>
      }
      footer={
        <Button
          onClick={() => this.setState({ uploadError: false })}
          className='slds-align_absolute-center'>
          OK
        </Button>
      }
    />
  )

  showProcessingUpload = () => (
    <Modal
      body={
        <Spinner type='track'>
          Processing
        </Spinner>
      }
    />
  )

  getDeductingWorkflowRunStepId = (stepNumber) => {
    let workflowRunStep = this.props.workflowRunDeducting.workflowrunsteps.edges.find(({ node: workflowRunStep }) => (
      workflowRunStep.workflowstep.number === stepNumber
    ))

    if (workflowRunStep) {
      return workflowRunStep.node.id
    } else {
      return null
    }
  }

  getSidebarActionButtons = () => {
    let deductingWorkflowAtStep = this.props.workflowRunDeducting
      ? this.props.workflowRunDeducting.atStep
      : null

    if (deductingWorkflowAtStep) {
      let deductingWorkflowRunStep7Id = this.getDeductingWorkflowRunStepId(7)
      let deductingWorkflowRunStep9Id = this.getDeductingWorkflowRunStepId(9)

      let regeneratePayrollCycleButtonDisabled = deductingWorkflowAtStep > 10
      let resendAmountApprovalButtonDisabled = deductingWorkflowAtStep !== 8
      let resendPaymentApprovalButtonDisabled = deductingWorkflowAtStep !== 10

      return [
        <ButtonRegeneratePayrollCycle
          workflowRunId={this.props.workflowRunDeducting.id}
          disabled={regeneratePayrollCycleButtonDisabled}
          onSuccess={this.refetchData}
          onFail={data => Sentry.captureMessage(`Deducting workflow run reset failed - ${data}`, 'error')}
        />,
        <ButtonResendAmountApprovalEmail
          workflowRunStep7Id={deductingWorkflowRunStep7Id}
          disabled={resendAmountApprovalButtonDisabled || deductingWorkflowRunStep7Id === null}
          onSuccess={this.refetchData}
          onFail={() => Sentry.captureMessage(`Resend of amount approval email failed.`, 'error')}
        />,
        <ButtonResendPaymentApprovalEmail
          workflowRunStep9Id={deductingWorkflowRunStep9Id}
          disabled={resendPaymentApprovalButtonDisabled || deductingWorkflowRunStep9Id === null}
          onSuccess={this.refetchData}
          onFail={() => Sentry.captureMessage(`Resend of payment approval email failed`, 'error')}
        />,
        <ButtonDownloadDeductionSpreadsheet
          workflowRunId={this.props.workflowRunDeducting.id}
          disabled={!this.props.paymentsSummary}
          onSuccess={() => this.setState({ showDownloadSuccessModal: true })}
          onFail={() => Sentry.captureMessage('Deduction spreadsheet download request failed.', 'error')}
        />,
        <ButtonUploadDeductionSpreadsheet
          workflowRunId={this.props.workflowRunDeducting.id}
          disabled={this.props.uploadButtonDisabled}
          inProgress={() => this.setState({ showProcessingUpload: true })}
          onSuccess={(file, data) => {
            this.setState({
              fileUploaded: file.name,
              uploadChanges: data.changes,
              uploadPrevented: data.prevented,
              uploadErrors: data.errors,
              uploadAmbiguous: data.ambiguous,
              uploadConfirm: true,
              showProcessingUpload: false
            })
          }}
          onFail={() => {
            Sentry.captureMessage('Deduction spreadsheet upload failed.', 'error')
            this.setState({ showProcessingUpload: false, uploadError: true })
          }}
        />
      ]
    } else {
      return []
    }
  }

  render() {
    let { workflowRunDeducting, workflowRunReceipting, paymentsSummary, paymentsTotal } = this.props
    return (
      <div className='slds-grid slds-size_1-of-1' >
        <div className='slds-col slds-size_8-of-12'>
          <WorkflowRunSteps
            title='Deducting workflow'
            workflowRunId={workflowRunDeducting
              ? workflowRunDeducting.id
              : null
            }
            hideActionButtons={true}
          />
          <WorkflowRunSteps
            title='Receipting workflow'
            workflowRunId={workflowRunReceipting
              ? workflowRunReceipting.id
              : null
            }
            hideActionButtons={true}
          />
        </div>
        {paymentsTotal
          ? <ActionsSummaryBar
              data={paymentsSummary}
              grandTotal={paymentsTotal}
              actions={this.getSidebarActionButtons()}
              className='slds-col slds-text-color_inverse side-panel is-deduction-summary'
            />
          : null
        }
        {this.state.showProcessingUpload && this.showProcessingUpload()}
        {this.state.uploadConfirm && this.showUploadConfirmModal()}
        {this.state.uploadError && this.showUploadError()}
        {this.state.showDownloadSuccessModal && this.showDownloadSuccessModal()}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/payroll-run-summary-view/index.js